/**
 * Map for boosting amenities/popular facilities. We're essentially copying the map from svc-search
 * https://github.com/lux-group/svc-search/blob/master/src/lib/amenities.ts
 *
 * How this works in svc-search:
 * Source Data                    →  AmenityMapping  →  OpenSearch Index (this is what CP gets)
 * ─────────────────────────────────────────────────────────────────────
 *
 * Examples:
 * - "Nespresso coffee machine"     →  "Coffee machine"  →  room_rate_config.amenities
 * - "Heated plunge pool"           →  "Private pool"    →  room_rate_config.amenities
 * - "Outdoor pool"                 →  "Private pool"    →  room_rate_config.amenities
 * - "Walk-in shower"               →  "Rain shower"     →  room_rate_config.amenities
 *
 * In Customer Portal:
 * - The map values (right side) represent standardized amenity categories (e.g., "Private pool", "Rain shower", "Coffee machine")
 * - These 20 unique category values are used as a proxy for matching against active amenity filters
 * - When a user has an active amenity filter, matching amenities are boosted to the top of search tiles
 * - Filter state is tracked via redux historicalAction (see useFilterChangeTracking in HotelSearchPageFilterPane)
 */
export const STANDARDISED_AMENITIES_MAP: Record<string, string> = {
  'Air conditioning': 'Air conditioning',
  'Air Conditioning': 'Air conditioning',
  'Balcony': 'Balcony',
  'Furnished balcony': 'Balcony',
  'Private balcony': 'Balcony',
  'Private veranda': 'Balcony',
  'Bath': 'Bathtub',
  'Bath tub': 'Bathtub',
  'Bathtub': 'Bathtub',
  'Bathtub and shower': 'Bathtub',
  'Outdoor bathtub': 'Bathtub',
  'Separate bathtub': 'Bathtub',
  'Separate bathtub and shower': 'Bathtub',
  'Separate shower and bathtub': 'Bathtub',
  'Shower and bathtub': 'Bathtub',
  'Sunken tub': 'Bathtub',
  'City views': 'City views',
  'Bella coffee machine': 'Coffee machine',
  'Coffee machine': 'Coffee machine',
  'Coffee maker': 'Coffee machine',
  'Coffesso coffee machine': 'Coffee machine',
  'Espresso coffee machine': 'Coffee machine',
  'Keurig coffee maker': 'Coffee machine',
  'Nespresso coffee machine': 'Coffee machine',
  'Nespresso coffee machine and a selection of T2 teas and hot chocolate': 'Coffee machine',
  'Nespresso coffee machine with complimentary Nespresso coffee and tea': 'Coffee machine',
  'Desk': 'Desk',
  'Desk with myriad charging ports': 'Desk',
  'Desk/office space': 'Desk',
  'Executive desk': 'Desk',
  'Full-size executive writing desk': 'Desk',
  'Large working desk': 'Desk',
  'Luxury business desk': 'Desk',
  'Spacious work desk': 'Desk',
  'Work desk': 'Desk',
  'Work desk and chair': 'Desk',
  'Work desk with data ports': 'Desk',
  'Work desk with multi-national electric and data ports': 'Desk',
  'Workdesk': 'Desk',
  'Workspace': 'Desk',
  'Garden views': 'Garden views',
  'Full kitchen': 'Kitchen',
  'Fully equipped kitchen': 'Kitchen',
  'Fully-equipped kitchen': 'Kitchen',
  'Fully equipped kitchenette': 'Kitchenette',
  'Laundry facilities': 'Laundry facilities',
  'Mountain views': 'Mountain views',
  'Mountain Views': 'Mountain views',
  'Beach views': 'Ocean views',
  'Beachfront': 'Ocean views',
  'Ocean views': 'Ocean views',
  'Partial ocean views': 'Ocean views',
  'Outdoor Furniture': 'Outdoor space',
  'Terrace': 'Outdoor space',
  'Garden': 'Outdoor space',
  'Patio': 'Outdoor space',
  'Courtyard': 'Outdoor space',
  'Pool views': 'Pool views',
  'Free onsite private parking': 'Private parking',
  'Free private parking': 'Private parking',
  'Private Parking': 'Private parking',
  'Private parking': 'Private parking',
  'Heated plunge pool': 'Private pool',
  'Infinity pool': 'Private pool',
  'Plunge pool': 'Private pool',
  'Plunge Pool': 'Private pool',
  'Pool': 'Private pool',
  'Private freshwater pool': 'Private pool',
  'Private infinity pool': 'Private pool',
  'Private jetpool': 'Private pool',
  'Private outdoor Jacuzzi pool': 'Private pool',
  'Private outdoor pool': 'Private pool',
  'Private outdoor pool and terrace': 'Private pool',
  'Private plunge pool': 'Private pool',
  'Private pool': 'Private pool',
  'Private pool, pool': 'Private pool',
  'Private swimming pool': 'Private pool',
  'Spa pool': 'Private pool',
  'Indoor rain shower': 'Rain shower',
  'Outdoor rain shower': 'Rain shower',
  'Outdoor shower': 'Rain shower',
  'Rain shower': 'Rain shower',
  'Rainfall showerhead': 'Rain shower',
  'Rainshower': 'Rain shower',
  'Walk-in shower': 'Rain shower',
  'Hot tub': 'Spa bath',
  'Jacuzzi': 'Spa bath',
  'Jet tub': 'Spa bath',
  'Spa bath': 'Spa bath',
  'Private outdoor deck': 'Terrace',
  'Private patio': 'Terrace',
  'Bay views': 'Water views',
  'Canal views': 'Water views',
  'Lagoon views': 'Water views',
  'Lake views': 'Water views',
  'River or ocean views': 'Water views',
  'Water views': 'Water views',
  '24-hour pool access': 'Private pool',
  'Access to nearby indoor pool': 'Private pool',
  'Access to nearby outdoor pool': 'Private pool',
  "Children's pool": 'Private pool',
  'Coffee/tea maker': 'Coffee machine',
  'Exercise/lap pool': 'Private pool',
  'Heated pool': 'Private pool',
  'Indoor/outdoor pool': 'Private pool',
  'Indoor pool': 'Private pool',
  'Indoor pool (surcharge)': 'Private pool',
  'Indoor private mineral hot springs (Japanese-style onsen)': 'Private pool',
  'Kitchen': 'Kitchen',
  'Kitchenette': 'Kitchenette',
  'Lazy river': 'Private pool',
  'Natural pool': 'Private pool',
  'Outdoor pool': 'Private pool',
  'Outdoor pool (surcharge)': 'Private pool',
  'Outdoor seasonal pool': 'Private pool',
  'Rooftop pool': 'Private pool',
  'Shared/communal pool': 'Private pool',
  'Spa tub': 'Spa bath',
  'Spa tub (surcharge)': 'Spa bath',
  'Swimming pool': 'Private pool',
  'Wheelchair-accessible pool': 'Private pool'
}
